import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, HelpCircle, ArrowRight, AlertCircle, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { READINESS_LABELS } from "@/lib/i18n";
import { limitsFor } from "@/lib/plan";
import LockedCard from "@/components/LockedCard";

const STATUS_STYLE = {
  "Güçlü": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Orta": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Zayıf": "bg-orange-500/15 text-orange-300 border-orange-500/30",
  "Eksik": "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function QuestionTests() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const limits = limitsFor(user);
  const visibleCap = limits.ai_questions_visible;
  const [scan, setScan] = useState(null);
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/businesses/${id}`).then((r) => r.data),
      api.get(`/scans/${id}/latest`, { params: { lang } }).then((r) => r.data).catch(() => null),
    ]).then(([b, s]) => { setBiz(b); setScan(s); setLoading(false); });
  }, [id, lang]);

  if (loading) return <div className="text-slate-400 flex items-center"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>;
  if (!scan) {
    return (
      <div className="glass-strong rounded-3xl p-10 text-center">
        <HelpCircle size={48} className="mx-auto text-cyan-400 mb-4" strokeWidth={1.4} />
        <h2 className="font-display text-2xl font-semibold text-white mb-2">{t("qt.noScanTitle")}</h2>
        <Link to={`/app/isletme/${id}/analiz`} className="neon-btn rounded-xl px-6 py-3 text-white font-semibold inline-flex items-center gap-2 mt-4">
          {t("dash.noScan.cta")} <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const allTests = scan.ai_question_tests || [];
  const tests = visibleCap > 0 ? allTests.slice(0, visibleCap) : allTests;
  const hiddenQuestionCount = allTests.length - tests.length;

  return (
    <div className="space-y-6" data-testid="question-tests-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">
          <HelpCircle size={12} /> {t("qt.eyebrow")}
        </div>
        <h1 className="font-display text-4xl font-bold text-white">{biz?.name}</h1>
        <p className="text-slate-400 mt-2 max-w-3xl leading-relaxed">{t("qt.subtitle")}</p>
      </div>

      <div className="grid gap-3">
        {tests.length === 0 && <div className="glass rounded-2xl p-8 text-center text-slate-400">{t("qt.empty")}</div>}
        {tests.map((q, i) => (
          <div key={i} className="glass-strong rounded-2xl p-5 card-glow" data-testid={`question-${i}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="text-white font-medium text-base flex-1 min-w-[260px]">"{q.question}"</div>
              <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border shrink-0 ${STATUS_STYLE[q.readiness] || ""}`}>
                {READINESS_LABELS[lang]?.[q.readiness] || q.readiness}
              </span>
            </div>

            {q.reason && <p className="text-sm text-slate-400 mb-3">{q.reason}</p>}

            {q.missing_signal && (
              <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 flex gap-2">
                <AlertCircle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-100">
                  <strong className="text-amber-300">{t("qt.missingSignal")}</strong> {q.missing_signal}
                </div>
              </div>
            )}

            {q.suggested_task_title && (
              <div className="mb-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20 p-3 text-xs text-slate-200">
                <strong className="text-cyan-300">{t("qt.suggestedTask")}</strong> {q.suggested_task_title}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {q.content_type && q.content_type !== "none" && (
                <button
                  onClick={() => navigate(`/app/isletme/${id}/icerik?type=${q.content_type}`)}
                  className="ghost-btn rounded-lg px-3 py-1.5 text-xs text-white inline-flex items-center gap-1.5"
                  data-testid={`question-${i}-content`}
                >
                  <Sparkles size={12} /> {t("qt.contentBtn")}
                </button>
              )}
              <Link to={`/app/isletme/${id}/gorevler`} className="ghost-btn rounded-lg px-3 py-1.5 text-xs text-white">
                {t("qt.gotoTasks")}
              </Link>
            </div>
          </div>
        ))}
      </div>

      {hiddenQuestionCount > 0 && (
        <LockedCard
          title={t("qt.locked.title", { n: hiddenQuestionCount })}
          description={t("qt.locked.desc")}
          requiredPlan="Starter"
          testid="qtests-locked-card"
        />
      )}
    </div>
  );
}
